import { useTranslation } from 'react-i18next';
import MarkdownContent from './MarkdownContent';

interface OptionButtonProps {
  letter: string;
  text: string;
  state: 'default' | 'selected' | 'correct' | 'incorrect' | 'disabled' | 'hinted';
  onClick: () => void;
  /** ARIA role override: use "radio" when inside a radiogroup */
  role?: 'radio' | 'button';
}

/* Variant B answer rows — flat white cards, slate border, blue selection ring.
   State is signalled by border/ring color + the letter tile, never by glass. */
const stateClasses: Record<OptionButtonProps['state'], string> = {
  default:
    'border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_1px_1px_rgba(15,23,42,0.04)] hover:border-slate-300 hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark dark:hover:border-white/20',
  selected:
    'border border-blue-600 bg-blue-50/50 ring-[3px] ring-blue-600/15 dark:border-blue-500 dark:bg-blue-500/10 dark:ring-blue-500/20',
  correct:
    'border border-green-600 bg-green-50/60 ring-[3px] ring-green-600/15 dark:border-green-500 dark:bg-green-500/10 dark:ring-green-500/20',
  incorrect:
    'border border-red-600 bg-red-50/60 ring-[3px] ring-red-600/15 dark:border-red-500 dark:bg-red-500/10 dark:ring-red-500/20',
  disabled:
    'border border-slate-200/70 bg-white text-slate-400 cursor-not-allowed opacity-60 dark:border-white/10 dark:bg-card-dark dark:text-slate-600',
  hinted:
    'border border-green-400/70 bg-green-50/40 ring-[3px] ring-green-500/10 dark:border-green-500/50 dark:bg-green-500/5',
};

const letterClasses: Record<OptionButtonProps['state'], string> = {
  default: 'border border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300',
  disabled: 'border border-slate-200 bg-slate-50 text-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-600',
  selected: 'bg-blue-600 text-white',
  correct: 'bg-green-600 text-white',
  incorrect: 'bg-red-600 text-white',
  hinted: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
};

export default function OptionButton({ letter, text, state, onClick, role }: OptionButtonProps) {
  const { t } = useTranslation();
  const isDisabled = state === 'disabled' || state === 'correct' || state === 'incorrect';
  const isRadio = role === 'radio';

  return (
    <button
      type="button"
      role={isRadio ? 'radio' : undefined}
      onClick={onClick}
      disabled={isDisabled}
      className={`flex w-full items-start gap-3 rounded-[13px] p-4 text-left transition-all sm:p-5 ${stateClasses[state]}`}
      aria-checked={isRadio ? state === 'selected' || state === 'correct' : undefined}
      aria-pressed={!isRadio ? state === 'selected' : undefined}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-extrabold font-mono ${letterClasses[state]}`}
      >
        {letter}
      </span>
      <MarkdownContent
        inline
        content={text}
        className="pt-0.5 text-sm leading-relaxed text-slate-800 dark:text-slate-200"
      />
      {state === 'correct' && (
        <span className="ml-auto shrink-0 font-extrabold text-green-600 dark:text-green-400" aria-label={t('study.optionButton.correct')}>✓</span>
      )}
      {state === 'incorrect' && (
        <span className="ml-auto shrink-0 font-extrabold text-red-600 dark:text-red-400" aria-label={t('study.optionButton.incorrect')}>✗</span>
      )}
    </button>
  );
}
